import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DashboardSkeleton } from '../../components/UI/SkeletonLoader';
import { AnimatedContainer, StaggeredList, HoverAnimation } from '../../components/UI/Animations';
import { EnhancedCard } from '../../components/UI/EnhancedComponents';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PlusIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { clientsAPI, facilitiesAPI, placementsAPI, tasksAPI } from '../../services/api';

interface DashboardStats {
  clients: {
    total: number;
    new: number;
    active: number;
  };
  facilities: {
    total: number;
    available: number;
    capacity: number;
  };
  placements: {
    total: number;
    active: number;
    pending: number;
    completed: number;
  };
  tasks: {
    total: number;
    pending: number;
    completed: number;
    overdue: number;
  };
}

const Dashboard: React.FC = () => {
  // Fetch dashboard data from backend
  const { data: clientsData, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients-dashboard'],
    queryFn: () => clientsAPI.getClientsDashboard(),
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const { data: facilitiesData, isLoading: facilitiesLoading } = useQuery({
    queryKey: ['facilities-dashboard'],
    queryFn: () => facilitiesAPI.getFacilitiesDashboard()
  });

  const { data: placementsData, isLoading: placementsLoading } = useQuery({
    queryKey: ['placements-dashboard'],
    queryFn: () => placementsAPI.getPlacementsDashboard()
  });

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks-dashboard'],
    queryFn: () => tasksAPI.getTasksDashboard()
  });

  const { data: swimLanesData, isLoading: swimLanesLoading } = useQuery({
    queryKey: ['swim-lanes'],
    queryFn: () => tasksAPI.getSwimLanes()
  });

  const isLoading = clientsLoading || facilitiesLoading || placementsLoading || tasksLoading;

  // Quick stats cards data
  const statsCards = [
    {
      title: 'Total Clients',
      value: clientsData?.data?.data?.totalClients || 0,
      change: clientsData?.data?.data?.statusCounts?.['New Client'] || 0,
      changeType: 'increase',
      icon: UserGroupIcon,
      color: 'primary',
      link: '/clients'
    },
    {
      title: 'Active Placements',
      value: placementsData?.data?.data?.activePlacements || 0,
      change: placementsData?.data?.data?.totalPlacements || 0,
      changeType: 'increase',
      icon: ClipboardDocumentListIcon,
      color: 'secondary',
      link: '/placements'
    },
    {
      title: 'Available Facilities',
      value: facilitiesData?.data?.data?.totalFacilities || 0,
      change: (facilitiesData?.data?.data?.occupancyStats?.totalCapacity || 0) - (facilitiesData?.data?.data?.occupancyStats?.totalOccupied || 0),
      changeType: 'neutral',
      icon: BuildingOfficeIcon,
      color: 'accent',
      link: '/facilities'
    },
    {
      title: 'Pending Tasks',
      value: (tasksData?.data?.data?.overview?.totalTasks || 0) - (tasksData?.data?.data?.overview?.completedTasks || 0),
      change: tasksData?.data?.data?.overview?.overdueTasks || 0,
      changeType: 'decrease',
      icon: CheckCircleIcon,
      color: 'red',
      link: '/tasks'
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary':
        return 'text-primary-600 bg-primary-50';
      case 'secondary':
        return 'text-secondary-600 bg-secondary-50';
      case 'accent':
        return 'text-accent-600 bg-accent-50';
      case 'red':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  if (isLoading) {
    return (
      <AnimatedContainer animation="fadeIn">
        <DashboardSkeleton />
      </AnimatedContainer>
    );
  }

  return (
    <AnimatedContainer animation="fadeIn" className="space-y-8">
      {/* Welcome Header */}
      <AnimatedContainer animation="slideDown" delay={100}>
        <EnhancedCard className="bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-100">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome to your Dashboard
          </h1>
          <p className="text-gray-600 mb-4">
            Here's an overview of your senior placement management system
          </p>
          
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Link
              to="/swim-lanes"
              className="btn-primary inline-flex items-center"
            >
              <Squares2X2Icon className="w-4 h-4 mr-2" />
              View Swim Lanes
            </Link>
            <Link
              to="/clients"
              className="btn-secondary inline-flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Client
            </Link>
            <Link
              to="/placements"
              className="btn-outline inline-flex items-center"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View All Placements
            </Link>
          </div>
        </EnhancedCard>
      </AnimatedContainer>

      {/* Stats Cards */}
      <AnimatedContainer animation="slideUp" delay={200}>
        <StaggeredList staggerDelay={100} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((card, index) => {
            const Icon = card.icon;
            const colorClasses = getColorClasses(card.color);
            
            return (
              <Link key={index} to={card.link}>
                <HoverAnimation animation="lift" intensity="medium">
                  <EnhancedCard className="group">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 rounded-xl ${colorClasses}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        {card.changeType === 'increase' && (
                          <ArrowUpIcon className="w-4 h-4 text-green-500 mr-1" />
                        )}
                        {card.changeType === 'decrease' && (
                          <ArrowDownIcon className="w-4 h-4 text-red-500 mr-1" />
                        )}
                        <span>{card.change}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {card.value.toLocaleString()}
                      </h3>
                      <p className="text-sm font-medium text-gray-600">
                        {card.title}
                      </p>
                    </div>
                  </EnhancedCard>
                </HoverAnimation>
              </Link>
            );
          })}
        </StaggeredList>
      </AnimatedContainer>

      {/* Swim Lane Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">
              Swim Lane Pipeline
            </h3>
            <Link
              to="/swim-lanes"
              className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
            >
              View Full Board
              <ArrowUpIcon className="w-4 h-4 ml-1 rotate-45" />
            </Link>
          </div>
          
          {swimLanesLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <div className="space-y-4">
              {swimLanesData?.data?.data?.lanes ? (
                Object.entries(swimLanesData.data.data.lanes).map(([stageName, stageData]: [string, any], index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">{stageName}</span>
                    <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-sm font-medium">
                      {stageData.count || 0}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Squares2X2Icon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No placement data available</p>
                  <Link
                    to="/placements"
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Create your first placement
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">
            Recent Activity
          </h3>
          
          <div className="space-y-4">
            {placementsData?.data?.data?.recentPlacements?.slice(0, 3).map((placement: any, index: number) => (
              <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className={`w-2 h-2 rounded-full ${
                  placement.status === 'In Progress' ? 'bg-blue-500' :
                  placement.status === 'Confirmed' ? 'bg-green-500' :
                  placement.status === 'Completed' ? 'bg-green-600' :
                  'bg-gray-500'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {placement.clientId?.firstName} {placement.clientId?.lastName} - {placement.status}
                  </p>
                  <p className="text-xs text-gray-500">
                    {placement.facilityId?.name} • {new Date(placement.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )) || (
              <div className="text-center py-8 text-gray-500">
                <p className="text-sm">No recent activity</p>
              </div>
            )}
            
            <div className="text-center pt-4">
              <Link
                to="/placements"
                className="text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                View all placements
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AnimatedContainer>
  );
};

export default Dashboard;
